import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { DatabaseZap } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MigrationCard = ({ getAuthHeader }) => {
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);

  const run = async () => {
    if (!window.confirm('Migrate all applications into the Loan Book (loan_applications)?\n\n• customer_applications is READ-ONLY — nothing is changed or deleted there\n• A backup of loan_applications is created first\n• Records already migrated are skipped (safe to run twice)')) return;
    setRunning(true);
    try {
      const res = await axios.post(`${API_URL}/admin/migrate-customer-apps-to-loans`, {}, getAuthHeader());
      setReport(res.data);
      toast.success(`Migration complete: ${res.data.migrated} migrated, ${res.data.skipped_existing} skipped`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Migration failed'); }
    setRunning(false);
  };

  return (
    <Card className="border-l-4 border-l-indigo-500" data-testid="migration-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2"><DatabaseZap className="w-4 h-4 text-indigo-600" />Migrate Applications → Loan Book</CardTitle>
        <p className="text-xs text-muted-foreground">Copies every customer application into the loan book (Origination / Servicing / Reports screens). Read-only on the source, backs up the loan book first, and skips records already migrated — safe to run more than once.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button onClick={run} disabled={running} className="bg-indigo-600 hover:bg-indigo-700" data-testid="run-migration-btn">
          {running ? 'Migrating…' : 'Run Migration'}
        </Button>
        {report && (
          <div className="text-sm space-y-2 p-3 bg-slate-50 rounded-lg" data-testid="migration-report">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <div><p className="text-xs text-slate-500">Loan book before → after</p><p className="font-semibold">{report.loan_applications_before} → {report.loan_applications_after}</p></div>
              <div><p className="text-xs text-slate-500">Migrated</p><p className="font-semibold text-emerald-700">{report.migrated}</p></div>
              <div><p className="text-xs text-slate-500">Skipped (already existed)</p><p className="font-semibold">{report.skipped_existing}</p></div>
              <div><p className="text-xs text-slate-500">Sum of migrated loan amounts</p><p className="font-semibold">R{(report.migrated_loan_amount_sum || 0).toLocaleString()}</p></div>
              <div><p className="text-xs text-slate-500">Source (customer_applications)</p><p className="font-semibold">{report.customer_applications_count} records {report.customer_applications_untouched && <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-0 text-[10px] ml-1">untouched</Badge>}</p></div>
              <div><p className="text-xs text-slate-500">Backup</p><p className="font-mono text-[11px]">{report.backup_collection || '—'}</p></div>
            </div>
            {report.pre_existing_records?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-1">Pre-existing records (not modified — delete manually if needed):</p>
                <div className="max-h-48 overflow-y-auto border rounded">
                  <table className="w-full text-xs">
                    <thead><tr className="text-left text-slate-500 border-b"><th className="p-1.5">Ref</th><th className="p-1.5">Name</th><th className="p-1.5">Amount</th><th className="p-1.5">Status</th></tr></thead>
                    <tbody>
                      {report.pre_existing_records.map(r => (
                        <tr key={r.id} className="border-b border-slate-100">
                          <td className="p-1.5 font-mono">{r.application_number || r.id?.slice(0, 8)}</td>
                          <td className="p-1.5">{r.name}</td>
                          <td className="p-1.5">R{(r.loan_amount || 0).toLocaleString()}</td>
                          <td className="p-1.5">{r.status}{r.migrated_from ? ' (migrated earlier)' : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
